import React from 'react';
import { Box, Typography, Chip } from '@mui/material';
import { styled, keyframes } from '@mui/material/styles';
import SchoolIcon from '@mui/icons-material/School';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import PsychologyIcon from '@mui/icons-material/Psychology';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import shapeIcon from '../../assets/images/icon/1.png';

// ============================================================
// ANIMATIONS
// ============================================================

const fadeInLeft = keyframes`
  from {
    opacity: 0;
    transform: translateX(-40px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

const fadeInRight = keyframes`
  from {
    opacity: 0;
    transform: translateX(40px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const pulse = keyframes`
  0% {
    box-shadow: 0 0 0 0 rgba(10, 79, 213, 0.4);
  }
  70% {
    box-shadow: 0 0 0 15px rgba(10, 79, 213, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(10, 79, 213, 0);
  }
`;

const float = keyframes`
  0% {
    transform: translateY(0px);
  }
  50% {
    transform: translateY(-10px);
  }
  100% {
    transform: translateY(0px);
  }
`;

// ============================================================
// COLOR PALETTE
// ============================================================

const DEEP_BLUE = "#0a1f44";
const PRIMARY_BLUE = "#0a4fd5";
const LIGHT_BLUE = "#4a9eff";
const SOFT_BLUE = "#e8f2ff";
const BORDER_BLUE = "#d4e5ff";
const TEXT_MUTED = "#5a6b85";

// ============================================================
// STYLED COMPONENTS
// ============================================================

const SectionWrapper = styled(Box)({
  position: "relative",
  padding: "80px 20px",
  backgroundColor: "#ffffff",
  overflow: "hidden",
});

const Container = styled(Box)({
  maxWidth: "1100px",
  margin: "0 auto",
  position: "relative",
  zIndex: 2,
});

const SectionHeader = styled(Box)({
  textAlign: "center",
  maxWidth: "700px",
  margin: "0 auto 60px",
  animation: `${fadeInUp} 0.8s ease forwards`,
});

const TimelineWrapper = styled(Box)({
  position: "relative",
  paddingTop: "20px",
});

const TimelineLine = styled(Box)({
  position: "absolute",
  top: 0,
  bottom: 0,
  left: "50%",
  width: 4,
  transform: "translateX(-50%)",
  background: `linear-gradient(180deg, ${PRIMARY_BLUE} 0%, ${LIGHT_BLUE} 100%)`,
  borderRadius: 2,
  opacity: 0.15,

  "@media (max-width: 768px)": {
    left: 20,
    transform: "none",
  },
});

const PhaseRow = styled(Box)(({ align }) => ({
  display: "flex",
  justifyContent: align === "right" ? "flex-end" : "flex-start",
  marginBottom: "40px",
  position: "relative",

  "@media (max-width: 768px)": {
    justifyContent: "flex-start",
    paddingLeft: "60px",
  },
}));

const PhaseCard = styled(Box)(({ align }) => ({
  width: "calc(50% - 50px)",
  backgroundColor: "#ffffff",
  borderRadius: "20px",
  padding: "28px 32px",
  border: `1px solid ${BORDER_BLUE}`,
  boxShadow: "0 10px 30px rgba(10, 31, 68, 0.06)",
  position: "relative",
  cursor: "pointer",
  transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
  animation: `${align === "right" ? fadeInRight : fadeInLeft} 0.8s ease forwards`,

  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    height: 4,
    background: `linear-gradient(90deg, ${PRIMARY_BLUE}, ${LIGHT_BLUE})`,
    borderRadius: "20px 20px 0 0",
    opacity: 0,
    transition: "opacity 0.3s ease",
  },

  "&:hover": {
    transform: "translateY(-6px)",
    boxShadow: "0 20px 50px rgba(10, 79, 213, 0.15)",
    borderColor: PRIMARY_BLUE,

    "&::before": {
      opacity: 1,
    },
  },

  "@media (max-width: 768px)": {
    width: "100%",
  },
}));

const TimelineDot = styled(Box)(({ color }) => ({
  position: "absolute",
  top: 32,
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 60,
  height: 60,
  borderRadius: "50%",
  backgroundColor: color || PRIMARY_BLUE,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  color: "#ffffff",
  zIndex: 3,
  border: "5px solid #ffffff",
  boxShadow: "0 8px 25px rgba(10, 79, 213, 0.35)",
  animation: `${pulse} 2.5s infinite`,
  transition: "all 0.3s ease",

  "& svg": {
    fontSize: 26,
  },

  "@media (max-width: 768px)": {
    left: 20,
    transform: "translate(-50%, -50%)",
    width: 48,
    height: 48,

    "& svg": {
      fontSize: 20,
    },
  },
}));

const PhaseHeader = styled(Box)({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  marginBottom: "14px",
  gap: 12,
});

const PhaseTitle = styled(Typography)({
  fontWeight: 700,
  color: DEEP_BLUE,
  fontSize: "1.2rem",
  lineHeight: 1.3,
});

const PhaseCode = styled(Chip)({
  backgroundColor: SOFT_BLUE,
  color: PRIMARY_BLUE,
  fontWeight: 700,
  fontSize: "12px",
  letterSpacing: "1px",
  border: `1px solid ${BORDER_BLUE}`,
  height: 28,
});

const PhaseDescription = styled(Typography)({
  color: TEXT_MUTED,
  lineHeight: 1.75,
  fontSize: "14.5px",
});

const DecorativeShape = styled(Box)({
  position: "absolute",
  bottom: -30,
  right: -30,
  width: 200,
  height: 200,
  opacity: 0.15,
  animation: `${float} 4s ease-in-out infinite`,
  pointerEvents: "none",
  zIndex: 0,

  "& img": {
    width: "100%",
    height: "100%",
    objectFit: "contain",
  },

  "@media (max-width: 768px)": {
    display: "none",
  },
});

const DecorativeDot = styled(Box)(({ size, top, left, right, bottom, delay, color }) => ({
  position: "absolute",
  width: size,
  height: size,
  borderRadius: "50%",
  backgroundColor: color || PRIMARY_BLUE,
  top,
  left,
  right,
  bottom,
  opacity: 0.08,
  animation: `${float} ${3 + (delay || 0)}s ease-in-out infinite`,
  animationDelay: `${delay || 0}s`,
  pointerEvents: "none",
  zIndex: 1,
}));

// ============================================================
// MAIN COMPONENT
// ============================================================

const Roadmap = () => {
  const phases = [
    {
      title: 'Learn the Basics',
      code: 'B1',
      level: 'Beginner',
      desc: 'Start your Forex journey by understanding what Forex is, how currency pairs work, what pips and lots mean, and how to read basic price charts. Get familiar with market sessions and the major currency pairs.',
      align: 'right',
      icon: <SchoolIcon />,
      color: PRIMARY_BLUE,
    },
    {
      title: 'Technical Analysis',
      code: 'B2',
      level: 'Beginner',
      desc: 'Learn to read candlestick patterns, support and resistance levels, and trend lines. Understand how to spot chart patterns and use basic indicators like moving averages, RSI, and MACD.',
      align: 'left',
      icon: <MenuBookIcon />,
      color: LIGHT_BLUE,
    },
    {
      title: 'Fundamental Analysis',
      code: 'I1',
      level: 'Intermediate',
      desc: 'Dive into economic news, interest rates, GDP, inflation, and central bank policies. Learn how global events move the markets and how to trade the news like a professional.',
      align: 'right',
      icon: <ShowChartIcon />,
      color: PRIMARY_BLUE,
    },
    {
      title: 'Strategy Development',
      code: 'I2',
      level: 'Intermediate',
      desc: 'Build and backtest your own trading strategy. Learn about risk management, position sizing, entry and exit rules, and how to journal your trades for continuous improvement.',
      align: 'left',
      icon: <TrendingUpIcon />,
      color: LIGHT_BLUE,
    },
    {
      title: 'Master Trading Psychology',
      code: 'A1',
      level: 'Advanced',
      desc: 'Master your emotions and discipline. Learn to handle winning streaks and losing streaks, overcome fear and greed, and develop the mindset of a consistently profitable trader.',
      align: 'right',
      icon: <PsychologyIcon />,
      color: PRIMARY_BLUE,
    },
    {
      title: 'Become a Pro Trader',
      code: 'A2',
      level: 'Advanced',
      desc: 'Trade with confidence and consistency. Manage a real account, scale your positions, mentor others, and access our VIP signals. Welcome to the top 5% of Forex traders!',
      align: 'left',
      icon: <EmojiEventsIcon />,
      color: LIGHT_BLUE,
    },
  ];

  const getLevelColor = (level) => {
    switch (level) {
      case 'Beginner':
        return { bg: "#e8f5e9", color: "#2e7d32" };
      case 'Intermediate':
        return { bg: "#fff3e0", color: "#e65100" };
      case 'Advanced':
        return { bg: "#e8f2ff", color: PRIMARY_BLUE };
      default:
        return { bg: SOFT_BLUE, color: PRIMARY_BLUE };
    }
  };

  return (
    <SectionWrapper id="roadmap">
      {/* Decorative Background Elements */}
      <DecorativeDot size={150} top="10%" left="-50px" delay={0} color={PRIMARY_BLUE} />
      <DecorativeDot size={100} bottom="15%" right="-20px" delay={1.5} color={LIGHT_BLUE} />
      <DecorativeDot size={80} top="40%" right="5%" delay={0.8} color={PRIMARY_BLUE} />

      <Container>
        {/* Section Header */}
        <SectionHeader>
          <Typography
            sx={{
              color: PRIMARY_BLUE,
              fontWeight: 700,
              fontSize: "13px",
              letterSpacing: "2px",
              textTransform: "uppercase",
              mb: 2,
            }}
          >
            Your Trading Journey
          </Typography>

          <Typography
            variant="h3"
            sx={{
              fontWeight: 800,
              color: DEEP_BLUE,
              lineHeight: 1.25,
              fontSize: { xs: "1.8rem", sm: "2.1rem", md: "2.5rem" },
              mb: 2,
            }}
          >
            From{" "}
            <Box
              component="span"
              sx={{
                color: PRIMARY_BLUE,
              }}
            >
              Beginner
            </Box>{" "}
            to Pro Trader
          </Typography>

          <Typography
            sx={{
              color: TEXT_MUTED,
              lineHeight: 1.8,
              fontSize: "16px",
            }}
          >
            Follow our proven learning path that has helped over 5,000 traders
            go from complete beginners to consistently profitable Forex
            professionals.
          </Typography>
        </SectionHeader>

        {/* Timeline */}
        <TimelineWrapper>
          <TimelineLine />

          {phases.map((phase, index) => {
            const levelStyle = getLevelColor(phase.level);
            return (
              <PhaseRow key={index} align={phase.align}>
                <PhaseCard align={phase.align}>
                  {/* Phase Header */}
                  <PhaseHeader>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1.5,
                        flex: 1,
                        minWidth: 0,
                      }}
                    >
                      <PhaseTitle>{phase.title}</PhaseTitle>
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        gap: 0.8,
                        alignItems: "center",
                        flexShrink: 0,
                      }}
                    >
                      <Chip
                        label={phase.level}
                        size="small"
                        sx={{
                          backgroundColor: levelStyle.bg,
                          color: levelStyle.color,
                          fontWeight: 600,
                          fontSize: "10.5px",
                          height: 22,
                          textTransform: "uppercase",
                          letterSpacing: "0.5px",
                        }}
                      />
                      <PhaseCode label={phase.code} size="small" />
                    </Box>
                  </PhaseHeader>

                  {/* Phase Description */}
                  <PhaseDescription>{phase.desc}</PhaseDescription>
                </PhaseCard>

                {/* Center Dot Icon */}
                <TimelineDot color={phase.color}>
                  {phase.icon}
                </TimelineDot>
              </PhaseRow>
            );
          })}
        </TimelineWrapper>
      </Container>

      {/* Bottom Decorative Shape */}
      <DecorativeShape>
        <img src={shapeIcon} alt="shape icon" />
      </DecorativeShape>
    </SectionWrapper>
  );
};

export default Roadmap;